/**
 * Friday wordmark — the movie logo, re-imagined.
 *
 * Rather than hand-tracing the bitmap (which always ends up with a couple
 * of letters reading as the wrong glyph), we use SVG `<text>` with the
 * heavy display Google Fonts "Bowlby One" + "Bowlby One SC" loaded in
 * index.html. The strokes are faked with `paint-order: stroke fill`
 * + a thick outer stroke + a thinner inner accent fill, both driven by
 * CSS variables so every theme recolours the mark instantly.
 *
 * Two variants:
 *   - default: full "friday" wordmark
 *   - compact:  stylised "Fr" mark (sidebar / drawer / favicon-ish)
 */

import { cn } from '@/lib/utils'

interface FridayMarkProps {
  size?: number
  className?: string
  compact?: boolean
  title?: string
}

/**
 * The fake ink outline is drawn in viewBox units, so it scales with the
 * rendered size. Bowlby One is already a very heavy face: at the auth-page
 * size (64) the 12-unit outline reads as inked line work, but on a 30px
 * navbar mark the same outline closes the letter counters and the wordmark
 * turns into a blob.
 *
 * So the outline shrinks faster than the mark does. Size 64 keeps the
 * original 12 units and is pixel-identical to before; smaller marks get
 * proportionally less outline and stay legible.
 */
function outlineWidth(size: number): number {
  const reference = 12 * Math.pow(size / 64, 0.66)
  return Math.round(Math.min(12, Math.max(2.5, reference)) * 10) / 10
}

export function FridayMark({
  size = 32,
  className,
  compact = false,
  title = 'Friday',
}: FridayMarkProps) {
  const width = compact ? size : size * 3.6
  const height = compact ? size : size * 1
  const strokeWidth = outlineWidth(size)
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={compact ? '0 0 200 200' : '0 0 720 200'}
      role="img"
      aria-label={title}
      className={cn('friday-mark shrink-0 select-none', className)}
    >
      <title>{title}</title>
      <defs>
        <style>{`
          .friday-text {
            font-family: 'Bowlby One', 'Fredoka', 'Inter', sans-serif;
            font-weight: 900;
            paint-order: stroke fill;
            stroke: var(--brand-stroke, currentColor);
            stroke-linejoin: round;
            stroke-linecap: round;
            fill: var(--brand-fill, currentColor);
            letter-spacing: -0.04em;
          }
          .friday-text.compact {
            font-size: 200px;
          }
          .friday-text.full {
            font-size: 200px;
          }
        `}</style>
      </defs>
      {compact ? (
        <text
          x="100"
          y="160"
          textAnchor="middle"
          className="friday-text compact"
          strokeWidth={strokeWidth}
        >
          Fr
        </text>
      ) : (
        <text
          x="360"
          y="160"
          textAnchor="middle"
          className="friday-text full"
          strokeWidth={strokeWidth}
        >
          friday
        </text>
      )}
    </svg>
  )
}